import { CreditCardsTablePagination } from './credit-cards-data-source.token';

/**
 * Data transfer object for credit card bill amounts.
 */
export interface AmountDto {
  amount: string;
  currency: string;
}

/**
 * Data transfer object for credit card bill as returned by the API.
 */
export interface CreditCardBillDto {
  pk_core: string;
  external_id?: string;
  credit_card_account_id?: string;
  lifecycle_status: string;
  due_date?: string;
  reference_month: number;
  reference_year: number;
  bill_total_amount?: AmountDto;
  bill_balance?: AmountDto;
  bill_minimum_amount?: AmountDto;
  is_instalment?: boolean;
  is_projected?: boolean;
}

/**
 * Data transfer object for credit card bills list response as returned by the API.
 */
export interface CreditCardBillsListResponse {
  has_more: boolean;
  results: CreditCardBillDto[];
}

/**
 * Backend category expense entry returned by the totals endpoint.
 */
export interface CategoryExpenseDto {
  category_name: string;
  total_amount: AmountDto;
  percentage: number;
}

/**
 * Backend response shape for total expenses per category endpoint.
 */
export interface TotalExpensesPerCategoryResponse {
  expenses: CategoryExpenseDto[];
}

/**
 * Mapping of backend lifecycle status strings to frontend numeric status codes.
 */
export const LIFECYCLE_STATUS_MAP: Record<string, number> = {
  OPEN: 0,
  CLOSED: 1,
  OVERDUE: 2,
  PAID: 3,
  PARTIALLY_PAID: 4,
  FUTURE: 5,
};

/**
 * Color palette applied to category bars and legend markers, in display order.
 */
export const CATEGORY_PALETTE: readonly string[] = [
  '#f39200',
  '#54bbab',
  '#e47272',
  '#038299',
  '#ce97bb',
  '#c5de31',
  '#ef765e',
  '#00437a',
  '#5cb26e',
  '#6dbafa',
];

/**
 * Color reserved for the aggregated "Outras Categorias" entry.
 */
export const OTHER_CATEGORIES_COLOR = '#d0e0e3';

/**
 * Category name treated as the "uncategorized" entry and decorated with an info icon.
 */
export const UNCATEGORIZED_LABEL = 'Não categorizadas';

/**
 * Tooltip text shown next to the "Não categorizadas" info icon.
 */
export const UNCATEGORIZED_TOOLTIP =
  'Transações que serão categorizadas assim que o processamento da compra for confirmado.';

/**
 * Maximum number of named categories before remaining ones are aggregated as "Outras Categorias".
 */
export const MAX_LEGEND_ITEMS = 10;

/**
 * Legend row rendered next to the stacked bar chart.
 */
export interface CategoryLegendItem {
  name: string;
  value: number;
  percent: number;
  color: string;
  showInfo: boolean;
}

/**
 * Monetary amount returned by the transactions endpoint (same shape as AmountDto).
 */
export type Amount = AmountDto;

/**
 * Merchant category attached to a transaction. Mirrors the gRPC contract
 * (`@celerofinancas/grpc-clients` → MerchantCategory = { code, category }).
 * Kept optional as a defensive boundary shape.
 */
export interface MerchantCategory {
  code?: string;
  category?: string;
}

/**
 * Credit card transaction as returned by the transactions list endpoint.
 */
export interface CreditCardTransactionDto {
  pk_core: string;
  external_id?: string;
  credit_card_account_id: string;
  bill_id?: string;
  transaction_name: string;
  credit_debit_type: string;
  transaction_type: string;
  brazilian_amount: Amount;
  original_amount: Amount;
  transaction_date_time: string;
  payee_mcc?: number;
  merchant_category?: MerchantCategory;
  current_instalment?: number;
  total_instalments?: number;
  payment_type?: string;
  bill_post_date?: string;
  updated_at?: unknown;
}

/**
 * Paginated response shape for the transactions list endpoint.
 */
export interface CreditCardTransactionsResponse {
  count: number;
  page: number;
  next_page: number;
  results: CreditCardTransactionDto[];
}

/**
 * Flat row consumed by the data-table. The index signature keeps it assignable
 * to the data-table's DataTableRow (Record<string, ...>) without coupling this
 * file to the shared component's interface.
 */
export interface CreditCardTransactionRow {
  lancamento: string;
  parcela: string;
  tipo: string;
  categoria: string;
  realizadoEm: string;
  /** Pre-formatted BRL string ("R$ 48,90" / "- R$ 27,75" / "–"). Rendered as
   * neutral dark text — the design uses no positive/negative coloring. */
  valor: string;
  [key: string]: string | number | null | undefined;
}

/**
 * Query options for fetching paginated transactions of a bill.
 */
export interface CreditCardTransactionsQuery {
  billId: string;
  page: number;
  pageSize: number;
}

/**
 * Normalized transactions result (rows + pagination).
 */
export interface CreditCardTransactionsResult {
  items: CreditCardTransactionRow[];
  pagination: CreditCardsTablePagination;
}

/**
 * Maps the backend `payment_type` (payment modality) to the "Tipo de transação"
 * column label. Keyed by the normalized value (uppercased, accent-stripped,
 * separators removed — see `normalizePaymentType`). Unknown/empty values fall
 * back to "–"; the raw value is never displayed.
 *
 * NOTE: `payment_type` is a free string in the contract (no enum). The backend
 * sends Portuguese values `A_VISTA` / `A_PRAZO` (confirmed against real data —
 * card 86e1x59d3); the remaining keys are defensive synonyms. New ones go here.
 */
export const PAYMENT_TYPE_LABEL: Record<string, string> = {
  AVISTA: 'À vista',
  CASH: 'À vista',
  APRAZO: 'Parcelada',
  INSTALMENT: 'Parcelada',
  INSTALLMENT: 'Parcelada',
  PARCELADA: 'Parcelada',
  PARCELADO: 'Parcelada',
};

/**
 * Normalizes a raw enum-like string for case/accent-insensitive comparison:
 * strips diacritics, removes spaces/underscores/punctuation and uppercases.
 * "À vista" / "a_vista" / "A VISTA" all collapse to "AVISTA".
 *
 * @param value raw value
 * @returns normalized token
 */
export function normalizeEnumToken(value?: string): string {
  return (value ?? '')
    .normalize('NFD')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/**
 * Normalizes a raw `payment_type` for lookup in {@link PAYMENT_TYPE_LABEL}.
 *
 * @param value raw payment_type
 * @returns normalized key
 */
export function normalizePaymentType(value?: string): string {
  return normalizeEnumToken(value);
}

/**
 * Normalized `transaction_type` value that marks a refund/chargeback entry.
 * Refunds carry no payment modality, instalment or merchant category, so the
 * classification columns (Parcela / Tipo de transação / Categoria) must all
 * render as "–" regardless of what the backend fills in.
 */
export const TRANSACTION_REFUND_TYPE = 'ESTORNO';

/**
 * Whether a transaction is a refund (estorno), by its `transaction_type`.
 *
 * @param dto transaction DTO (only `transaction_type` is read)
 * @returns true when the entry is a refund
 */
export function isRefundTransaction(
  dto: Pick<CreditCardTransactionDto, 'transaction_type'>,
): boolean {
  return normalizeEnumToken(dto.transaction_type) === TRANSACTION_REFUND_TYPE;
}

/**
 * `credit_debit_type` prefix (normalized) that marks a credit entry — i.e. a
 * refund, which renders the amount as negative. The backend sends the
 * Portuguese "CREDITO"/"DEBITO"; older/defensive payloads may send "CREDIT".
 */
export const TRANSACTION_CREDIT_TYPE = 'CREDIT';

/**
 * Whether a transaction is a credit (renders the amount negative), matching
 * both "CREDIT" and the Portuguese "CREDITO" on `credit_debit_type`.
 *
 * @param dto transaction DTO (only `credit_debit_type` is read)
 * @returns true when the amount should be shown negative
 */
export function isCreditTransaction(
  dto: Pick<CreditCardTransactionDto, 'credit_debit_type'>,
): boolean {
  return normalizeEnumToken(dto.credit_debit_type).startsWith(TRANSACTION_CREDIT_TYPE);
}

/**
 * Page size options shown in the transactions table footer.
 */
export const TRANSACTIONS_PAGE_SIZE_OPTIONS = [10, 25, 50];
